import * as XLSX from "xlsx";
import type { Brand, Student } from "@/types";
import { normalizePhone } from "./importValidate";
import { getRegionConfig, parseGuFrom, isInRegion, sidoShort } from "./region";

// 구 이름 없이 지명만 적힌 부산 강서구 명지/신호 일대 추론 (예: "명지 오션시티", "신호산단")
// org 권역이 부산일 때만 적용 — 타 지역 org에서 오탐 방지
const GANGSEO_HINTS: { hint: string; dong: string }[] = [
  { hint: "명지국제", dong: "명지동" },
  { hint: "명지", dong: "명지동" },
  { hint: "신호", dong: "신호동" },
];
const isBusanRegion = () => getRegionConfig().sido === "부산광역시";

function cleanStr(s: any): string {
  if (s == null) return "";
  return String(s).replace(/\n/g, " ").trim();
}

function parseGu(addr: string): string {
  const g = parseGuFrom(addr);
  if (g) return g;
  if (isBusanRegion() && GANGSEO_HINTS.some((h) => addr.includes(h.hint))) return "강서구";
  return "";
}

function parseDong(refined: string): string {
  // 1) 괄호 안 법정동/행정동 우선 — "(민락동, ...)" "(동광동2가, ...)" (신뢰도 높음)
  const m = refined.match(/\(([가-힣]+\d*(?:동|가|리|읍|면))(?:,|\))/); // 읍·면 지역(청도읍 등)도 동 칸으로
  if (m) return m[1];
  // 2) 괄호 밖 — 한글로 시작하는 동/가/리만 매칭해 건물 동("110동")을 법정동으로 오인하지 않도록 함
  const m2 = refined.match(/([가-힣]+\d*(?:동|가|리|읍|면))(?:[,\s\)]|$)/);
  if (m2) return m2[1];
  // 구 이름 없이 지명만 있는 부산 강서구 명지/신호 일대 법정동 보완
  const hint = isBusanRegion() ? GANGSEO_HINTS.find((h) => refined.includes(h.hint)) : undefined;
  return hint ? hint.dong : "";
}

function parseApt(refined: string): string {
  const m = refined.match(/\([^,]+,([^)]+)\)/);
  return m ? m[1].trim() : "";
}

// 도로명 + 건물번호(번길/가길/번지/하이픈 포함)를 통째로 제거 — "샘플G대로 635-13"의 건물번호를 동-호로 오인하지 않게.
function stripRoad(t: string): string {
  return t.replace(/[가-힣A-Za-z0-9·]*(?:대로|로|길)\s*\d+(?:\s*번\s*[가-힣]?\s*가?길\s*\d+)?(?:-\d+)?(?:번지)?/g, " ");
}

function parseUnit(refined: string, raw: string): { dong: string; ho: string } {
  // raw 우선(원문이 동·호를 보존), 없으면 refined. 도로명·지번을 먼저 제거해 동-호와의 충돌을 막는다.
  const src = raw && /\d/.test(raw) ? raw : refined || "";
  const txt = stripRoad(" " + src.replace(/[(),]/g, " ") + " ");
  // 1) 숫자 동 + 호 ("101동 202호").
  let m = txt.match(/(\d{1,3})\s*동\s*(\d{1,5})\s*호/);
  if (m) return { dong: m[1], ho: m[2] };
  // 2) 동 글자가 있으면 호 글자 유무 무관("T3동 4302", "307동 105"). 앞이 한글이면 행정동("개금1동")이라 제외.
  m = txt.match(/(?<![가-힣])([A-Za-z]\d?|\d{1,3})\s*동\s*(\d{1,5})\s*호?/);
  if (m) return { dong: m[1], ho: m[2] };
  // 3) 한글 동(가/나/다동)은 건물 동 라벨로 실제 쓰이는 가나다 순서 글자로만 제한하고,
  // 동 글자 앞 공백/구분자 경계를 요구해 "우동/좌동/온천동" 같은 법정동 오인을 막는다.
  m = txt.match(/(?:^|[\s,])([가나다라마바사아자차카타파하])\s*동\s*(\d{1,5})\s*호?/);
  if (m) return { dong: m[1], ho: m[2] };
  // 4) 영문 동 + 하이픈 호("A-449호", "B-1501호", "A-159"). "A동 449호"를 하이픈으로 줄여 쓴 표기다.
  // 이 규칙이 없으면 아래 5)가 호수만 집어가 동이 조용히 사라진다(2026-08-15 학생11·학생13·학생9).
  m = txt.match(/(?:^|[\s,])([A-Za-z])\s*-\s*(\d{1,5})\s*호?/);
  if (m) return { dong: m[1], ho: m[2] };
  // 5) 하이픈 동-호("103-1602", 호 2~4자리). 앞 경계로 도로명 잔재 숫자와 분리.
  m = txt.match(/(?:^|\s|[가-힣A-Za-z@·])\s*(\d{1,3})\s*-\s*(\d{2,4})(?:\s|호|$)/);
  if (m) return { dong: m[1], ho: m[2] };
  // 6) 단독 호("202호").
  m = txt.match(/(\d{2,5})\s*호/);
  if (m) return { dong: "", ho: m[1] };
  // 7) 주택·빌라 층("1층", "2층") — 호가 없을 때만. "층" 접미사를 포함해 저장해 호수와 구분한다.
  m = txt.match(/(\d{1,2})\s*층/);
  return { dong: "", ho: m ? `${m[1]}층` : "" };
}

/**
 * refinedAddress 만 새로 들어왔을 때(지오코더 매칭 주소 등) 단지명만 안전하게 재추출.
 * 지오코더 도로명주소엔 건물 동·호가 없어 함께 재파싱하면 동·호가 지워지므로, 단지명만 갱신한다.
 * 빈 문자열이면 기존 buildingName 을 유지하라는 뜻.
 */
export function deriveBuildingName(refined: string): string {
  return parseApt(refined);
}

/**
 * "N번길" 표기 정규화 — 띄어쓰기가 어긋난 주소를 표준 표기로(서버 파이프라인과 동일 규칙).
 * 예: "샘플B대로 76번길55" → "샘플B대로76번길 55".
 * 어긋난 표기를 그대로 저장·지오코딩하면 실존하는 다른 번길로 스냅된다(함정 #8).
 */
export function normalizeBungil(s: string): string {
  return s
    .replace(/(\d+)\s*번\s*([가-힣]?)\s*길/g, "$1번$2길")
    .replace(/((?:대로|로))\s+(\d+번[가-힣]?길)/g, "$1$2")
    .replace(/(번[가-힣]?길)(\d)/g, "$1 $2");
}

/**
 * 주소 문자열에서 구·법정동·건물명·동·호(층)를 재계산.
 * 주소 변경 등으로 refinedAddress를 갱신할 때 파생 필드를 함께 새로 채우는 용도.
 */
export function deriveAddressFields(refined: string, raw: string) {
  const { dong: bDong, ho: bHo } = parseUnit(refined, raw);
  return {
    gu: parseGu(refined),
    dong: parseDong(refined),
    buildingName: parseApt(refined),
    buildingDong: bDong,
    buildingHo: bHo,
  };
}

/**
 * 파일명으로 그룹 판별 (A/B/C). 그 외는 "X" — 일반 업로드.
 */
function detectGroup(filename: string): "A" | "B" | "C" | "X" {
  const n = filename.toUpperCase();
  if (n.startsWith("A")) return "A";
  if (n.startsWith("B")) return "B";
  if (n.startsWith("C")) return "C";
  return "X";
}

/**
 * 단일 시트를 파싱해 학생 레코드 배열로 변환.
 * A/B: No(0), 학교(1), 성명(2), 보호자(3), 연락처(4), 주소(5), 비고(6), ..., 우편번호(-2), 정제주소(-1)
 * C:   방문지주소(0), 작업시간(1), 학교(2), 학생(3), 노트(4), 연락처(5), ..., 우편번호(11), 정제주소(12)
 * X:   자동 헤더 매핑 — "학교","성명","이름","학생명","보호자","연락처","전화","주소","우편번호" 키워드
 */
export function parseSheetToStudents(
  rows: any[][],
  filename: string,
  sheetName: string,
  overrides: { brand?: Brand; company?: string; isProxy?: boolean; proxyFor?: string; legacyGroups?: boolean },
): Partial<Student>[] {
  const group = overrides.legacyGroups ? detectGroup(filename) : "X"; // 부산 교육청 A/B/C 고정 칸 규칙은 1호 업체에서만(예행연습 02-2, 04-12)
  const out: Partial<Student>[] = [];

  if (group === "X") {
    // 헤더 기반 매핑 — 맨 위에 "2026학년도 무상우유 명단" 같은 제목 줄이 있어도 건너뛰고,
    // 앞 15줄 중 '학교'와 '성명/이름/학생' 칸 제목이 함께 있는 줄을 머리줄로 쓴다(없으면 예전처럼 첫 줄).
    // 머리줄 = '학교' 칸과 '성명/이름/학생' 칸이 서로 다른 칸이고 둘 다 짧은 칸 제목 — 제목 문장("OO학교 학생 명단") 한 칸은 제외
    const isHeader = (r: any[]) => {
      const cells = r.map((c) => cleanStr(c));
      const iSchool = cells.findIndex((c) => c.length <= 8 && c.includes("학교"));
      const iName = cells.findIndex((c, i) => i !== iSchool && c.length <= 8 && ["성명", "이름", "학생"].some((k) => c.includes(k)));
      return iSchool >= 0 && iName >= 0;
    };
    const headerRow =
      rows.slice(0, 15).find((r) => Array.isArray(r) && isHeader(r)) ||
      rows.find((r) => Array.isArray(r) && r.some((c) => cleanStr(c)));
    if (!headerRow) return [];
    const headerIdx = rows.indexOf(headerRow);
    const headers = headerRow.map((h) => cleanStr(h));
    const col = (names: string[]) => headers.findIndex((h) => names.some((n) => h.includes(n)));

    const idxSchool = col(["학교"]);
    const idxName = col(["성명", "학생", "이름"]);
    const idxGuardian = col(["보호자", "학부모", "부모"]);
    const idxPhone = col(["연락처", "전화", "휴대", "핸드폰"]);
    const idxAddr = col(["주소"]);
    const idxPostal = col(["우편", "zip"]);
    const idxRefined = col(["정제", "표준주소", "도로명"]);
    const idxNote = col(["비고", "메모", "note"]);

    for (let i = headerIdx + 1; i < rows.length; i++) {
      const r = rows[i] || [];
      const school = cleanStr(r[idxSchool]);
      const name = cleanStr(r[idxName]);
      if (!name || !school) continue;
      const rawAddress = cleanStr(r[idxAddr]);
      if (!rawAddress && idxRefined < 0) continue;
      const refined = cleanStr(r[idxRefined]) || rawAddress;
      const postal = /^\d{5}$/.test(cleanStr(r[idxPostal])) ? cleanStr(r[idxPostal]) : "";
      out.push(makeStudent({
        sourceGroup: "X",
        sheet: sheetName,
        school,
        name,
        guardian: cleanStr(r[idxGuardian]),
        phone: normalizePhone(cleanStr(r[idxPhone])),
        rawAddress,
        refinedAddress: refined.startsWith(sidoShort()) ? refined : rawAddress,
        postalCode: postal,
        note: cleanStr(r[idxNote]),
      }, overrides));
    }
    return out;
  }

  // A/B/C — 기존 로직
  const isC = group === "C";
  const start = isC ? 1 : 2;
  for (let i = start; i < rows.length; i++) {
    const r = rows[i] || [];
    let name = "", school = "", guardian = "", phone = "", rawAddress = "", refined = "", postal = "", note = "";
    if (isC) {
      rawAddress = cleanStr(r[0]);
      school = cleanStr(r[2]);
      name = cleanStr(r[3]);
      phone = cleanStr(r[5]);
      postal = cleanStr(r[11]);
      refined = cleanStr(r[12]);
      note = cleanStr(r[4]);
    } else {
      school = cleanStr(r[1]);
      name = cleanStr(r[2]);
      guardian = cleanStr(r[3]);
      phone = cleanStr(r[4]);
      rawAddress = cleanStr(r[5]);
      note = cleanStr(r[6]);
      refined = cleanStr(r[r.length - 1]);
      postal = cleanStr(r[r.length - 2]);
    }
    if (!name || !rawAddress) continue;
    if (!/^\d{5}$/.test(postal)) postal = "";
    if (!refined.startsWith(sidoShort())) refined = rawAddress;

    out.push(makeStudent({
      sourceGroup: group,
      sheet: sheetName,
      school,
      name,
      guardian,
      phone: normalizePhone(phone),
      rawAddress,
      refinedAddress: refined,
      postalCode: postal,
      note,
    }, overrides));
  }
  return out;
}

function makeStudent(
  base: Partial<Student>,
  overrides: { brand?: Brand; company?: string; isProxy?: boolean; proxyFor?: string; legacyGroups?: boolean },
): Partial<Student> {
  const refined = base.refinedAddress || base.rawAddress || "";
  const derived = deriveAddressFields(refined, base.rawAddress || "");
  // 권역 외 주소(예: 부산 org의 김해)는 직배송 대신 월말 택배로 분리
  const inRegion = isInRegion(`${refined} ${base.rawAddress || ""}`, derived.gu || "");
  return {
    ...base,
    ...derived,
    brand: overrides.brand || "서울우유",
    company: overrides.company || "자사",
    isProxy: overrides.isProxy || false,
    proxyFor: overrides.proxyFor || "",
    status: "active",
    courierFlag: !inRegion,
    ...(inRegion ? {} : { courierReason: "always" as const }), // 권역 밖은 새 달에도 택배
    boxesPerMonth: 1,
  };
}

/**
 * 완전히 같은 행(학교+이름+주소+전화+동호)이 여러 번 나오면 1건으로 합침.
 * 엑셀에 "중복항목제거" 같은 보조 시트가 있어 같은 학생이 두 번 파싱되는 경우를 대응한다.
 *
 * 함정 #5(주소-단지묶음-알려진함정.md): 같은 단지에 같은 이름의 동명이인이 실존한다.
 * 아파트 주소엔 동·호가 없어 이름+주소만으로 합치면 다른 학생 하나가 조용히 사라진다.
 * → 전화번호·동·호가 전부 같을 때만 진짜 중복으로 보고, 하나라도 다르면 둘 다 보존한다.
 */
function dedupeStudents(list: Partial<Student>[]): Partial<Student>[] {
  const seen = new Set<string>();
  const out: Partial<Student>[] = [];
  for (const s of list) {
    const addr = (s.refinedAddress || s.rawAddress || "").replace(/[\s,.]/g, "");
    const phoneDigits = (s.phone || "").replace(/\D/g, "");
    const key = `${s.school || ""}|${s.name || ""}|${addr}|${phoneDigits}|${s.buildingDong || ""}|${s.buildingHo || ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

/**
 * 파일 ArrayBuffer → 학생 레코드 배열.
 * 여러 시트가 있으면 모두 병합. Sheet1 같은 요약 시트는 스킵.
 */
export async function parseFileToStudents(
  file: File,
  overrides: { brand?: Brand; company?: string; isProxy?: boolean; proxyFor?: string; legacyGroups?: boolean },
): Promise<Partial<Student>[]> {
  if (file.name.endsWith(".json")) {
    const text = await file.text();
    const arr = JSON.parse(text);
    return arr.map((s: any) => ({
      ...s,
      company: overrides.company || s.company || "자사",
      brand: overrides.brand || s.brand || "서울우유",
      isProxy: overrides.isProxy ?? s.isProxy ?? false,
      proxyFor: overrides.proxyFor || s.proxyFor || "",
    }));
  }

  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const out: Partial<Student>[] = [];
  for (const sheetName of wb.SheetNames) {
    // 요약 시트 스킵
    if (sheetName.toLowerCase() === "sheet1" && wb.SheetNames.length > 1) continue;
    const ws = wb.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1 });
    out.push(...parseSheetToStudents(rows, file.name, sheetName, overrides));
  }
  return dedupeStudents(out);
}
